import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {reduxToggleSidePanel} from 'appredux/actions';
import {
  apiDeleteFarmAsset,
  apiGetFdAssets,
  apiGetFdLiabilities,
  apiUpdateFdFinancialPerformance,
  apiUpsertFdLiability,
  apiUpsertFarmAsset,
  apiDeleteFdLiability,
} from 'constants/Api';
import {EAssetCondition, EAssetType, ESidePanelView} from 'constants/index';
import {
  IFdApplicationType,
  IFdFarmAssetList,
  IFdFarmAssetType,
  IGenericType,
  IFdLiability,
  IFdOperatingCost,
  IFdProductionCost,
} from 'constants/interfaces';

import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import ApplicationJourneySubStepProgress from './ApplicationJourneySubStepProgress';
import ApplicationFinancialsCashflowStep, {
  ApplicationFinancialsCashflowController,
} from './ApplicationFinancialsCashflowStep';
import ApplicationFinancialsFundingRequestStep, {
  ApplicationFinancialsFundingRequestController,
} from './ApplicationFinancialsFundingRequestStep';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';
import {
  lshUseAssetsSubStep,
  lshUseFinancialHistorySubStep,
  lshUseLiabilitiesSubStep,
} from './steps/financials/hooks';
import {
  AssetsSubStep,
  FinancialHistorySubStep,
  LiabilitiesSubStep,
} from './steps/financials/substeps';
import type {
  AssetConditionOption,
  AssetDraft,
  FinancialHistoryDraft,
  FinancialSubStepKey,
  LiabilityDraft,
} from './steps/financials/financials.types';
import {
  lshBuildFinancialYearOptions,
  lshCreateFinancialSummaryDrafts,
  lshFormatCompactCurrency,
  lshMapAssetToDraft,
  lshMapLiabilityToDraft,
  lshParseCurrency,
  lshToNumber,
  lshToText,
} from './steps/financials/financials.utils';
import {
  RevenueRow as SupplierRevenueRow,
  useFinancialData,
} from '../ApplicationTabs/FinancialInformationTab/hooks/useFinancialData';
import {
  BLANK,
  FiscalKey,
} from '../ApplicationTabs/FinancialInformationTab/cards/OperatingCostsCard/OperatingCostsGrid';
import {MONTHS as CALENDAR_MONTHS} from '../ApplicationTabs/FinancialInformationTab/utils/financialCalculations';
import {lshParseOperatingCostMonthlyCosts} from './steps/financials/financials.utils';

type CashflowRow = {
  id: string;
  label: string;
  values: Record<FiscalKey, number>;
  rowType: 'revenue' | 'production-cost' | 'operating-cost';
  productionCost?: IFdProductionCost;
  operatingCost?: IFdOperatingCost;
};

type CashflowActionState = ApplicationFinancialsCashflowController | null;
type FundingRequestActionState =
  ApplicationFinancialsFundingRequestController | null;

const MONTH_OPTIONS = [
  {value: '1', label: 'January'},
  {value: '2', label: 'February'},
  {value: '3', label: 'March'},
  {value: '4', label: 'April'},
  {value: '5', label: 'May'},
  {value: '6', label: 'June'},
  {value: '7', label: 'July'},
  {value: '8', label: 'August'},
  {value: '9', label: 'September'},
  {value: '10', label: 'October'},
  {value: '11', label: 'November'},
  {value: '12', label: 'December'},
];

const ASSET_CONDITIONS: Array<{
  value: AssetConditionOption;
  label: string;
}> = [
  {value: EAssetCondition.NEW, label: 'New'},
  {value: EAssetCondition.LIKE_NEW, label: 'Like new'},
  {value: EAssetCondition.GOOD, label: 'Good'},
  {value: EAssetCondition.FAIR, label: 'Fair'},
  {value: EAssetCondition.POOR, label: 'Poor'},
];

function lshCreateBlankFiscalValues(): Record<FiscalKey, number> {
  return {...BLANK} as Record<FiscalKey, number>;
}

function lshGetMonthKeys(): FiscalKey[] {
  return Object.keys(BLANK) as FiscalKey[];
}

function lshParseFiscalKey(key: FiscalKey): {monthIndex: number; year: number} {
  const monthPart = key.slice(0, 3).toLowerCase();
  const yearPart = Number(`20${key.slice(3)}`);
  return {
    monthIndex: CALENDAR_MONTHS.indexOf(monthPart as any),
    year: yearPart,
  };
}

// eslint-disable-next-line no-unused-vars
function lshFormatFiscalLabel(key: FiscalKey): string {
  return `${key.slice(0, 3).toUpperCase()} ${key.slice(3)}`;
}

function lshGetMonthIndexFromName(value: unknown): number | null {
  const text = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  if (!text) return null;

  const index = CALENDAR_MONTHS.findIndex(
    (month) => month === text.slice(0, 3),
  );
  return index >= 0 ? index : null;
}

function lshFindFiscalKeyForMonth(
  monthValue: unknown,
  year: number,
  monthKeys: FiscalKey[],
): FiscalKey | null {
  const monthIndex = lshGetMonthIndexFromName(monthValue);
  if (monthIndex === null) return null;

  const exactMatch =
    monthKeys.find((key) => {
      const parsed = lshParseFiscalKey(key);
      return parsed.monthIndex === monthIndex && parsed.year === year;
    }) || null;

  if (exactMatch) return exactMatch;

  return (
    monthKeys.find((key) => lshParseFiscalKey(key).monthIndex === monthIndex) ||
    null
  );
}

function lshDistributeAmountAcrossKeys(
  target: Record<FiscalKey, number>,
  keys: FiscalKey[],
  total: number,
) {
  if (keys.length === 0 || total <= 0) return;

  const perKey = Number((total / keys.length).toFixed(2));
  let allocated = 0;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      target[key] += Number((total - allocated).toFixed(2));
      return;
    }

    target[key] += perKey;
    allocated += perKey;
  });
}

function lshSumCashflowRows(
  rows: Array<{values: Record<FiscalKey, number>}>,
  monthKeys: FiscalKey[],
): Record<FiscalKey, number> {
  const summary = lshCreateBlankFiscalValues();

  rows.forEach((row) => {
    monthKeys.forEach((monthKey) => {
      summary[monthKey] += lshToNumber(row.values[monthKey]);
    });
  });

  return summary;
}

function lshBuildProductionCostRows(
  application: IFdApplicationType | undefined,
  monthKeys: FiscalKey[],
): CashflowRow[] {
  return (application?.productionCosts || []).flatMap((record, index) => {
    if (!record.plantingDate) return [];

    const plantingDate = new Date(record.plantingDate);
    if (Number.isNaN(plantingDate.getTime())) return [];

    plantingDate.setMonth(plantingDate.getMonth() - 1);

    const targetKey =
      monthKeys.find((key) => {
        const parsed = lshParseFiscalKey(key);
        return (
          parsed.monthIndex === plantingDate.getMonth() &&
          parsed.year === plantingDate.getFullYear()
        );
      }) || null;

    if (!targetKey) return [];

    return [
      {
        label: `${lshToText(record.crop?.name, 'Crop')} Seed`,
        cost: record.seed,
      },
      {
        label: `${lshToText(record.crop?.name, 'Crop')} Fertiliser`,
        cost: record.fertiliser,
      },
      {
        label: `${lshToText(record.crop?.name, 'Crop')} Chemical`,
        cost: record.chemical,
      },
    ]
      .filter((item) => lshToNumber(item.cost) > 0)
      .map((item, itemIndex) => {
        const values = lshCreateBlankFiscalValues();
        values[targetKey] = lshToNumber(item.cost);

        return {
          id: `${record.id || index}-${itemIndex}`,
          label: item.label,
          values,
          rowType: 'production-cost' as const,
          productionCost: record,
        };
      });
  });
}

function lshBuildOperatingCostValues(
  record: IFdOperatingCost,
  monthKeys: FiscalKey[],
): Record<FiscalKey, number> {
  const values = lshCreateBlankFiscalValues();
  const rowYear = lshToNumber(record.year) || new Date().getFullYear();

  try {
    const normalised = lshParseOperatingCostMonthlyCosts(record.monthlyCosts);
    if (Object.keys(normalised.monthlyValues).length > 0) {
      Object.entries(normalised.monthlyValues).forEach(([key, value]) => {
        const normalisedKey = key.toLowerCase() as FiscalKey;
        if (normalisedKey in values) {
          values[normalisedKey] += lshToNumber(value);
        }
      });

      const hasExplicitValues = monthKeys.some((key) => values[key] > 0);
      if (hasExplicitValues) return values;
    }

    const config = JSON.parse(record.monthlyCosts || '{}');
    const total = lshToNumber(config.total);
    const legacyMonthSelections = Array.isArray(config.months)
      ? config.months.filter(
          (entry: unknown) => typeof entry === 'string' && entry,
        )
      : [];
    const monthSelections = [...legacyMonthSelections, config.month].filter(
      Boolean,
    );
    const quarterSelections = Array.isArray(config.quarters)
      ? config.quarters
      : [];

    if (config.frequency === 'monthly' && monthSelections.length > 0) {
      monthSelections.forEach((selection: unknown) => {
        const key = lshFindFiscalKeyForMonth(selection, rowYear, monthKeys);
        if (key) {
          values[key] += total;
        }
      });
      return values;
    }

    if (config.frequency === 'quarterly' && quarterSelections.length > 0) {
      quarterSelections.forEach((quarter: string) => {
        const quarterNumber = lshToNumber(
          String(quarter).replace(/[^1-4]/g, ''),
        );
        if (!quarterNumber) return;

        const startMonth = (quarterNumber - 1) * 3;
        const quarterKeys = monthKeys.filter((monthKey) => {
          const parsed = lshParseFiscalKey(monthKey);
          return (
            parsed.year === rowYear &&
            parsed.monthIndex >= startMonth &&
            parsed.monthIndex < startMonth + 3
          );
        });

        lshDistributeAmountAcrossKeys(values, quarterKeys, total);
      });
      return values;
    }

    const onceOffKey =
      lshFindFiscalKeyForMonth(monthSelections[0], rowYear, monthKeys) ||
      monthKeys.find((key) => lshParseFiscalKey(key).year === rowYear) ||
      monthKeys[0];

    if (onceOffKey) {
      values[onceOffKey] += total;
    }
  } catch {
    return values;
  }

  return values;
}

function lshBuildOperatingCostRows(
  application: IFdApplicationType | undefined,
  monthKeys: FiscalKey[],
): CashflowRow[] {
  return (application?.operatingCosts || []).map((record, index) => ({
    id: String(record.id || index),
    label: lshToText(record.expense?.name, 'Operating cost'),
    values: lshBuildOperatingCostValues(record, monthKeys),
    rowType: 'operating-cost' as const,
    operatingCost: record,
  }));
}

const ApplicationFinancialsStep: React.FC = () => {
  const {
    application,
    applicationId,
    activeStep,
    activeSubStepIndex,
    goNext,
    goPrevious,
    goToSubStep,
    goToStep,
    refreshJourney,
    setApplication,
  } = useSupplierApplicationJourney();

  const currentApplication = application as IFdApplicationType | undefined;
  const {revenueRows} = useFinancialData(currentApplication);

  const monthKeys = useMemo(() => lshGetMonthKeys(), []);
  const yearOptions = useMemo(() => lshBuildFinancialYearOptions(), []);
  const applicationAssets = useMemo(
    () =>
      (
        currentApplication?.assets ||
        currentApplication?.farmAsset ||
        []
      ).filter(Boolean) as IFdFarmAssetType[],
    [currentApplication?.assets, currentApplication?.farmAsset],
  );
  const applicationLiabilities = useMemo(
    () =>
      (currentApplication?.liabilities || []).filter(Boolean) as IFdLiability[],
    [currentApplication?.liabilities],
  );

  const activeSubStep = activeStep.subSteps?.[activeSubStepIndex] || {
    key: 'financial-summary',
    title: 'Financial History',
    description:
      'Review historic financial performance year by year and add notes where necessary.',
  };

  const activeSubStepKey = activeSubStep.key as FinancialSubStepKey;
  const [financialSummaries, setFinancialSummaries] = useState<
    FinancialHistoryDraft[]
  >(() =>
    lshCreateFinancialSummaryDrafts(currentApplication?.financialPerformance),
  );
  const [isSavingFinancialHistory, setIsSavingFinancialHistory] =
    useState(false);
  const [financialHistoryStatus, setFinancialHistoryStatus] = useState('');
  const [assetCatalogue, setAssetCatalogue] = useState<IFdFarmAssetList[]>([]);
  const [assetRows, setAssetRows] = useState<AssetDraft[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isSavingAssets, setIsSavingAssets] = useState(false);
  const [assetStatus, setAssetStatus] = useState('');
  const [liabilityAccountTypes, setLiabilityAccountTypes] = useState<
    IGenericType[]
  >([]);
  const [liabilityRows, setLiabilityRows] = useState<LiabilityDraft[]>([]);
  const [isLoadingLiabilities, setIsLoadingLiabilities] = useState(false);
  const [isSavingLiabilities, setIsSavingLiabilities] = useState(false);
  const [liabilityStatus, setLiabilityStatus] = useState('');
  const [cashflowActionState, setCashflowActionState] =
    useState<CashflowActionState>(null);
  const [fundingRequestActionState, setFundingRequestActionState] =
    useState<FundingRequestActionState>(null);
  const {handleSummaryFieldChange} = lshUseFinancialHistorySubStep({
    setFinancialSummaries,
  });
  const {handleAddAsset, handleUpdateAssetRowField, validateAssetRow} =
    lshUseAssetsSubStep({
      assetCatalogue,
      setAssetRows,
    });
  const {
    handleAddLiability,
    handleUpdateLiabilityRowField,
    validateLiabilityRow,
  } = lshUseLiabilitiesSubStep({
    liabilityAccountTypes,
    setLiabilityRows,
  });

  useEffect(() => {
    setFinancialSummaries(
      lshCreateFinancialSummaryDrafts(currentApplication?.financialPerformance),
    );
  }, [currentApplication?.financialPerformance]);

  useEffect(() => {
    setAssetRows(applicationAssets.map(lshMapAssetToDraft));
  }, [applicationAssets]);

  useEffect(() => {
    setLiabilityRows(applicationLiabilities.map(lshMapLiabilityToDraft));
  }, [applicationLiabilities]);

  const revenueCashflowRows = useMemo(
    () =>
      (revenueRows || []).map((row: SupplierRevenueRow, index: number) => {
        const values = lshCreateBlankFiscalValues();
        monthKeys.forEach((monthKey) => {
          values[monthKey] += lshToNumber(row[monthKey]);
        });

        return {
          id: `${row.label || 'revenue'}-${index}`,
          label: lshToText(row.label, 'Projected revenue'),
          values,
          rowType: 'revenue' as const,
        };
      }),
    [monthKeys, revenueRows],
  );

  const productionCashflowRows = useMemo(
    () => lshBuildProductionCostRows(currentApplication, monthKeys),
    [currentApplication, monthKeys],
  );

  const operatingCashflowRows = useMemo(
    () => lshBuildOperatingCostRows(currentApplication, monthKeys),
    [currentApplication, monthKeys],
  );

  const revenueTotals = useMemo(
    () => lshSumCashflowRows(revenueCashflowRows, monthKeys),
    [monthKeys, revenueCashflowRows],
  );

  const productionTotals = useMemo(
    () => lshSumCashflowRows(productionCashflowRows, monthKeys),
    [monthKeys, productionCashflowRows],
  );

  const operatingTotals = useMemo(
    () => lshSumCashflowRows(operatingCashflowRows, monthKeys),
    [monthKeys, operatingCashflowRows],
  );

  // eslint-disable-next-line no-unused-vars
  const netCashflowTotals = useMemo(() => {
    const totals = lshCreateBlankFiscalValues();

    monthKeys.forEach((monthKey) => {
      totals[monthKey] =
        revenueTotals[monthKey] -
        productionTotals[monthKey] -
        operatingTotals[monthKey];
    });

    return totals;
  }, [monthKeys, operatingTotals, productionTotals, revenueTotals]);

  const numericApplicationId = Number(currentApplication?.id || applicationId);
  const numericCompanyId = Number(currentApplication?.company?.id || -1);
  const numericFarmId = Number(
    currentApplication?.farm?.id || applicationAssets[0]?.farm?.id || -1,
  );
  const canSubmitFinancialHistory =
    Number.isFinite(numericApplicationId) &&
    numericApplicationId > 0 &&
    Number.isFinite(numericCompanyId) &&
    numericCompanyId > 0 &&
    financialSummaries.length > 0;
  const canSubmitAssets =
    Number.isFinite(numericApplicationId) &&
    numericApplicationId > 0 &&
    Number.isFinite(numericFarmId) &&
    numericFarmId > 0 &&
    assetRows.length > 0;
  const canSubmitLiabilities =
    Number.isFinite(numericApplicationId) &&
    numericApplicationId > 0 &&
    liabilityRows.length > 0;

  useEffect(() => {
    if (!financialHistoryStatus) return;

    const timer = window.setTimeout(() => {
      setFinancialHistoryStatus('');
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [financialHistoryStatus]);

  useEffect(() => {
    if (!assetStatus) return;

    const timer = window.setTimeout(() => {
      setAssetStatus('');
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [assetStatus]);

  useEffect(() => {
    if (!liabilityStatus) return;

    const timer = window.setTimeout(() => {
      setLiabilityStatus('');
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [liabilityStatus]);

  useEffect(() => {
    let isActive = true;

    setIsLoadingAssets(true);
    apiGetFdAssets()
      .then((assets) => {
        if (!isActive) return;
        setAssetCatalogue(assets);
      })
      .catch((error) => {
        if (!isActive) return;
        setAssetStatus('Failed to load assets.');
        console.error('Failed to load asset catalogue', error);
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoadingAssets(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    setIsLoadingLiabilities(true);
    apiGetFdLiabilities()
      .then((liabilities) => {
        if (!isActive) return;
        setLiabilityAccountTypes(liabilities);
      })
      .catch((error) => {
        if (!isActive) return;
        setLiabilityStatus('Failed to load liability account types.');
        console.error('Failed to load liability account types', error);
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoadingLiabilities(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  const advanceStep = useCallback(() => {
    setApplication((current) => {
      if (!current) return current;

      return {
        ...current,
        progress: Math.max(Number(current.progress || 0), 6),
        subProgress: Math.max(
          Number(current.subProgress || 0),
          activeSubStepIndex + 1,
        ),
      };
    });

    goNext();
  }, [activeSubStepIndex, goNext, setApplication]);

  const handleSaveFinancialHistory = useCallback(async () => {
    if (!canSubmitFinancialHistory) {
      setFinancialHistoryStatus(
        'Missing company or application information for financial history.',
      );
      return;
    }

    try {
      setIsSavingFinancialHistory(true);

      await Promise.all(
        financialSummaries.map((record) => {
          return apiUpdateFdFinancialPerformance({
            year: record.year,
            revenue: lshParseCurrency(record.revenue),
            expenses: lshParseCurrency(record.expenses),
            operatingCosts: lshParseCurrency(record.operatingCosts),
            profit:
              lshParseCurrency(record.revenue) -
              lshParseCurrency(record.expenses) -
              lshParseCurrency(record.operatingCosts),
            financialYearEnd: record.financialYearEnd
              ? Number(record.financialYearEnd)
              : undefined,
            financialYearEndMonth: record.financialYearEndMonth
              ? Number(record.financialYearEndMonth)
              : undefined,
            notes: record.notes || '',
            companyId: numericCompanyId,
            applicationId: numericApplicationId,
          });
        }),
      );

      await refreshJourney();
      setFinancialHistoryStatus('Financial history saved.');
      advanceStep();
    } catch (error) {
      setFinancialHistoryStatus('Saving financial history failed.');
      alert(
        'Failed to update financial history at the moment. Please try again later',
      );
      console.error('Failed to update financial history', error);
    } finally {
      setIsSavingFinancialHistory(false);
    }
  }, [
    advanceStep,
    canSubmitFinancialHistory,
    financialSummaries,
    numericApplicationId,
    numericCompanyId,
    refreshJourney,
  ]);

  const handleDeleteAsset = useCallback(async (row: AssetDraft) => {
    try {
      if (row.backendId) {
        await apiDeleteFarmAsset(row.backendId);
      }
      setAssetRows((current) =>
        current.filter((item) => item.localId !== row.localId),
      );
      setAssetStatus('Asset deleted.');
    } catch (error) {
      setAssetStatus('Failed to delete asset.');
      alert('Failed to delete asset at the moment. Please try again later');
      console.error('Failed to delete asset', error);
    }
  }, []);

  const handleSaveAssets = useCallback(async () => {
    if (!canSubmitAssets) {
      setAssetStatus('Add at least one asset before continuing.');
      return;
    }

    for (let index = 0; index < assetRows.length; index += 1) {
      const rowError = validateAssetRow(assetRows[index]);
      if (rowError) {
        setAssetStatus(`Asset row ${index + 1}: ${rowError}`);
        return;
      }
    }

    try {
      setIsSavingAssets(true);

      const updatedRows: AssetDraft[] = [];
      for (const row of assetRows) {
        const response = await apiUpsertFarmAsset({
          id: row.backendId,
          description: row.description,
          year: row.year || undefined,
          condition: row.condition || undefined,
          value: lshParseCurrency(row.value),
          pricePerUnit:
            row.pricePerUnit && row.pricePerUnit.trim().length > 0
              ? lshParseCurrency(row.pricePerUnit)
              : undefined,
          farmId: numericFarmId,
          capacity: row.capacity,
          model: row.model,
          make: row.make,
          assetType:
            row.assetType && row.assetType.toUpperCase() === EAssetType.CAPEX
              ? EAssetType.CAPEX
              : EAssetType.ASSET,
          assetListId: Number(row.assetListId),
          applicationId: numericApplicationId,
        });

        updatedRows.push({
          ...row,
          value: lshParseCurrency(row.value).toFixed(2),
          backendId:
            Number((response as any)?.id || row.backendId) || row.backendId,
        });
      }

      setAssetRows(updatedRows);
      await refreshJourney();
      setAssetStatus('Assets saved successfully.');
      advanceStep();
    } catch (error) {
      setAssetStatus('Failed to save assets.');
      alert('Failed to update assets at the moment. Please try again later');
      console.error('Failed to save assets', error);
    } finally {
      setIsSavingAssets(false);
    }
  }, [
    advanceStep,
    assetRows,
    canSubmitAssets,
    numericApplicationId,
    numericFarmId,
    refreshJourney,
    validateAssetRow,
  ]);

  const handleDeleteLiability = useCallback(async (row: LiabilityDraft) => {
    try {
      if (row.backendId) {
        await apiDeleteFdLiability(row.backendId);
      }
      setLiabilityRows((current) =>
        current.filter((item) => item.localId !== row.localId),
      );
      setLiabilityStatus('Liability deleted.');
    } catch (error) {
      setLiabilityStatus('Failed to delete liability.');
      alert('Failed to delete liability at the moment. Please try again later');
      console.error('Failed to delete liability', error);
    }
  }, []);

  const handleSaveLiabilities = useCallback(async () => {
    if (!canSubmitLiabilities) {
      setLiabilityStatus('Add at least one liability before continuing.');
      return;
    }

    for (let index = 0; index < liabilityRows.length; index += 1) {
      const rowError = validateLiabilityRow(liabilityRows[index]);
      if (rowError) {
        setLiabilityStatus(`Liability row ${index + 1}: ${rowError}`);
        return;
      }
    }

    try {
      setIsSavingLiabilities(true);

      const updatedRows: LiabilityDraft[] = [];
      for (const row of liabilityRows) {
        const response = await apiUpsertFdLiability({
          id: row.backendId,
          creditLimitAmount: lshParseCurrency(row.creditLimitAmount),
          currentBalanceLimit: lshParseCurrency(row.currentBalanceLimit),
          monthlyInstallmentAmount: lshParseCurrency(
            row.monthlyInstallmentAmount,
          ),
          inArrears: row.inArrears,
          liabilityAccountTypeId: row.liabilityAccountTypeId!,
          lender: row.lender.trim(),
          applicationId: numericApplicationId,
        });

        updatedRows.push({
          ...row,
          lender: row.lender.trim(),
          creditLimitAmount: lshParseCurrency(row.creditLimitAmount).toFixed(2),
          currentBalanceLimit: lshParseCurrency(
            row.currentBalanceLimit,
          ).toFixed(2),
          monthlyInstallmentAmount: lshParseCurrency(
            row.monthlyInstallmentAmount,
          ).toFixed(2),
          backendId:
            Number((response as any)?.id || row.backendId) || row.backendId,
        });
      }

      setLiabilityRows(updatedRows);
      await refreshJourney();
      setLiabilityStatus('Liabilities saved successfully.');
      advanceStep();
    } catch (error) {
      setLiabilityStatus('Failed to save liabilities.');
      alert(
        'Failed to update liabilities at the moment. Please try again later',
      );
      console.error('Failed to save liabilities', error);
    } finally {
      setIsSavingLiabilities(false);
    }
  }, [
    advanceStep,
    canSubmitLiabilities,
    liabilityRows,
    numericApplicationId,
    refreshJourney,
    validateLiabilityRow,
  ]);

  const handleOpenProductionCost = (productionCost?: IFdProductionCost) => {
    if (!productionCost?.crop?.id) return;

    reduxToggleSidePanel(true, {
      panelType: ESidePanelView.EDIT_FD_PRODUCTION_COST,
      data: {
        applicationId: Number(currentApplication?.id || applicationId),
        productionCost,
        crop: productionCost.crop,
      },
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenOperatingCosts = () => {
    reduxToggleSidePanel(true, {
      panelType: ESidePanelView.EDIT_FD_OPERATING_COSTS,
      data: {
        applicationId: Number(currentApplication?.id || applicationId),
        operatingCosts: currentApplication?.operatingCosts || [],
      },
    });
  };

  const handleOpenOperatingCost = (operatingCost?: IFdOperatingCost) => {
    if (!operatingCost) return;

    reduxToggleSidePanel(true, {
      panelType: ESidePanelView.EDIT_FD_OPERATING_COST,
      data: {
        applicationId: Number(currentApplication?.id || applicationId),
        operatingCost,
      },
    });
  };

  const handleContinue = useCallback(() => {
    advanceStep();
  }, [advanceStep]);

  const handleCashflowComplete = useCallback(async () => {
    await refreshJourney();
    advanceStep();
  }, [advanceStep, refreshJourney]);

  const handleFundingRequestComplete = useCallback(async () => {
    await refreshJourney();
    advanceStep();
  }, [advanceStep, refreshJourney]);

  const renderFinancialSummary = () => (
    <FinancialHistorySubStep
      financialHistoryStatus={financialHistoryStatus}
      financialSummaries={financialSummaries}
      isSavingFinancialHistory={isSavingFinancialHistory}
      yearOptions={yearOptions}
      monthOptions={MONTH_OPTIONS}
      onSummaryFieldChange={handleSummaryFieldChange}
    />
  );

  const renderAssets = () => (
    <AssetsSubStep
      assetStatus={assetStatus}
      isLoadingAssets={isLoadingAssets}
      assetRows={assetRows}
      assetCatalogue={assetCatalogue}
      assetConditions={ASSET_CONDITIONS}
      isSavingAssets={isSavingAssets}
      onAddAsset={handleAddAsset}
      onUpdateAssetRowField={handleUpdateAssetRowField}
      onDeleteAsset={handleDeleteAsset}
    />
  );

  const renderLiabilities = () => (
    <LiabilitiesSubStep
      liabilityStatus={liabilityStatus}
      isLoadingLiabilities={isLoadingLiabilities}
      liabilityRows={liabilityRows}
      liabilityAccountTypes={liabilityAccountTypes}
      isSavingLiabilities={isSavingLiabilities}
      onAddLiability={handleAddLiability}
      onUpdateLiabilityRowField={handleUpdateLiabilityRowField}
      onDeleteLiability={handleDeleteLiability}
    />
  );

  // eslint-disable-next-line no-unused-vars
  const renderCashflowRows = (
    title: string,
    rows: CashflowRow[],
    totals: Record<FiscalKey, number>,
  ) => {
    const hasRows = rows.length > 0;

    return (
      <>
        <tr className="financial-cashflow-table__section">
          <th colSpan={monthKeys.length + 2}>{title}</th>
        </tr>
        {hasRows ? (
          rows.map((row) => (
            <tr key={row.id}>
              <th>{row.label}</th>
              {monthKeys.map((monthKey) => (
                <td key={`${row.id}-${monthKey}`}>
                  {row.values[monthKey] > 0
                    ? lshFormatCompactCurrency(row.values[monthKey])
                    : '-'}
                </td>
              ))}
              <td>
                {row.rowType === 'revenue' ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() =>
                      goToStep('production-info', {
                        subStepKey: 'production-plan',
                      })
                    }
                  >
                    Review
                  </button>
                ) : row.rowType === 'production-cost' ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => handleOpenProductionCost(row.productionCost)}
                    disabled={!row.productionCost?.crop?.id}
                  >
                    Edit
                  </button>
                ) : (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => handleOpenOperatingCost(row.operatingCost)}
                  >
                    Edit
                  </button>
                )}
              </td>
            </tr>
          ))
        ) : (
          <tr className="financial-cashflow-table__empty">
            <td colSpan={monthKeys.length + 2}>
              No {title.toLowerCase()} have been added to this application yet.
            </td>
          </tr>
        )}
        <tr className="financial-cashflow-table__total">
          <th>{title} total</th>
          {monthKeys.map((monthKey) => (
            <td key={`${title}-${monthKey}-total`}>
              {totals[monthKey] > 0
                ? lshFormatCompactCurrency(totals[monthKey])
                : '-'}
            </td>
          ))}
          <td />
        </tr>
      </>
    );
  };

  const renderCashflow = () => (
    <ApplicationFinancialsCashflowStep
      applicationId={numericApplicationId}
      application={currentApplication}
      onRegisterController={setCashflowActionState}
      onComplete={handleCashflowComplete}
    />
  );

  const renderFundingRequest = () => (
    <ApplicationFinancialsFundingRequestStep
      applicationId={numericApplicationId}
      application={currentApplication}
      onRegisterController={setFundingRequestActionState}
      onComplete={handleFundingRequestComplete}
    />
  );

  const renderSubStepContent = () => {
    if (activeSubStepKey === 'financial-summary') {
      return renderFinancialSummary();
    }

    if (activeSubStepKey === 'assets') {
      return renderAssets();
    }

    if (activeSubStepKey === 'liabilities') {
      return renderLiabilities();
    }

    if (activeSubStepKey === 'cashflow') {
      return renderCashflow();
    }

    return renderFundingRequest();
  };

  return (
    <>
      <ApplicationJourneySubStepProgress
        steps={(activeStep.subSteps || []).map(({key, title}) => ({
          key,
          title,
        }))}
        activeIndex={activeSubStepIndex}
        ariaLabel="Financials sub-steps"
        onStepClick={goToSubStep}
      />
      <ApplicationJourneyStepLayout
        title={activeSubStep.title}
        description={activeSubStep.description}
        secondaryAction={{
          label: 'Back',
          onClick: goPrevious,
          disabled:
            activeSubStepKey === 'financial-summary'
              ? isSavingFinancialHistory
              : activeSubStepKey === 'assets'
              ? isSavingAssets
              : activeSubStepKey === 'liabilities'
              ? isSavingLiabilities
              : activeSubStepKey === 'cashflow'
              ? Boolean(cashflowActionState?.isSaving)
              : activeSubStepKey === 'funding-request'
              ? Boolean(fundingRequestActionState?.isSaving)
              : false,
        }}
        primaryAction={{
          label:
            activeSubStepKey === 'financial-summary'
              ? 'Save & continue'
              : activeSubStepKey === 'assets'
              ? 'Save & continue'
              : activeSubStepKey === 'liabilities'
              ? 'Save & continue'
              : activeSubStepKey === 'cashflow'
              ? 'Save & continue'
              : activeSubStepKey === 'funding-request'
              ? 'Submit funding ask'
              : activeSubStepIndex === (activeStep.subSteps?.length || 1) - 1
              ? 'Continue to upload documents'
              : 'Continue',
          onClick:
            activeSubStepKey === 'financial-summary'
              ? handleSaveFinancialHistory
              : activeSubStepKey === 'assets'
              ? handleSaveAssets
              : activeSubStepKey === 'liabilities'
              ? handleSaveLiabilities
              : activeSubStepKey === 'cashflow'
              ? () => {
                  void cashflowActionState?.save();
                }
              : activeSubStepKey === 'funding-request'
              ? () => {
                  void fundingRequestActionState?.save();
                }
              : handleContinue,
          disabled:
            activeSubStepKey === 'financial-summary'
              ? !canSubmitFinancialHistory || isSavingFinancialHistory
              : activeSubStepKey === 'assets'
              ? !canSubmitAssets || isSavingAssets || isLoadingAssets
              : activeSubStepKey === 'liabilities'
              ? !canSubmitLiabilities ||
                isSavingLiabilities ||
                isLoadingLiabilities
              : activeSubStepKey === 'cashflow'
              ? !cashflowActionState?.canSubmit ||
                Boolean(cashflowActionState?.isLoading) ||
                Boolean(cashflowActionState?.isSaving)
              : activeSubStepKey === 'funding-request'
              ? !fundingRequestActionState?.canSubmit ||
                Boolean(fundingRequestActionState?.isLoading) ||
                Boolean(fundingRequestActionState?.isSaving)
              : false,
          loading:
            activeSubStepKey === 'financial-summary'
              ? isSavingFinancialHistory
              : activeSubStepKey === 'assets'
              ? isSavingAssets
              : activeSubStepKey === 'liabilities'
              ? isSavingLiabilities
              : activeSubStepKey === 'cashflow'
              ? Boolean(cashflowActionState?.isSaving)
              : activeSubStepKey === 'funding-request'
              ? Boolean(fundingRequestActionState?.isSaving)
              : false,
        }}
      >
        {renderSubStepContent()}
      </ApplicationJourneyStepLayout>
    </>
  );
};

export default ApplicationFinancialsStep;
